import React, { useEffect, useState } from "react";
import MultiSelect from "../../../../components/MultiSelect";
import { Option } from "../../../../interfaces/MultiSelectTypes";
import { getServices } from "../../../../apis/service";
import { getVisitServices, addVisitServices, updateVisitServices } from "../../../../apis/visit";
import { getClinicUsers } from "../../../../apis/user";
import { useNotification } from "../../../../NotificationContext";

interface ServiceModalProps {
    isOpen: boolean;
    onClose: () => void;
    onServiceAdded: (services: any[]) => void;
    visitId?: number;
}

interface ServiceItem {
    id?: number;
    service_id: number;
    name: string;
    fee: number;
    quantity: number;
    amount?: number;
    clinic_user_id?: number | null;
}

interface ClinicUser {
    id: number;
    clinicUserId?: number;
    firstName: string;
    lastName: string;
    phone?: string;
}

const ServiceModal: React.FC<ServiceModalProps> = ({
    isOpen,
    onClose,
    onServiceAdded,
    visitId,
}) => {
    const { showNotification } = useNotification();
    const [isOpenDropdown, setIsOpenDropdown] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [services, setServices] = useState<any[]>([]);
    const [selectedServices, setSelectedServices] = useState<ServiceItem[]>([]);
    const [clinicUsers, setClinicUsers] = useState<ClinicUser[]>([]);
    const [openDropdownServiceId, setOpenDropdownServiceId] = useState<number | null>(null);
    const [existingServices, setExistingServices] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [clinicUserSearchTerm, setClinicUserSearchTerm] = useState("");

    useEffect(() => {
        if (isOpen) {
            fetchServices();
            fetchClinicUsers();
            if (visitId) {
                fetchExistingServices();
            } else {
                setSelectedServices([]);
                setExistingServices([]);
            }
        }
    }, [isOpen, visitId]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            if (!target.closest('.clinic-user-dropdown-container')) {
                setOpenDropdownServiceId(null);
                setClinicUserSearchTerm("");
            }
        };

        if (openDropdownServiceId !== null) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [openDropdownServiceId]);

    const fetchServices = async (search: string = "") => {
        try {
            const {
                data: { data },
            } = await getServices({ name: search });
            setServices(data);
        } catch (err) {
            console.log(err);
        }
    };

    const fetchClinicUsers = async () => {
        try {
            const response: any = await getClinicUsers();
            setClinicUsers(response.data || []);
        } catch (err) {
            console.log(err);
        }
    };

    const fetchExistingServices = async () => {
        if (!visitId) return;
        try {
            setIsLoading(true);
            const response: any = await getVisitServices(visitId);
            const servicesData = response.data || [];
            setExistingServices(servicesData);
            
            // Transform existing services to match ServiceItem format
            const transformedServices: ServiceItem[] = servicesData
                .map((item: any) => {
                    // Backend responses can vary (snake_case vs camelCase vs nested service object)
                    const serviceId =
                        item.service_id ??
                        item.serviceId ??
                        item.service?.id ??
                        item.service?.service_id;

                    if (!serviceId) return null;

                    const fee = item.fee ?? 0;
                    const quantity = item.quantity ?? 1;

                    return {
                        id: item.id,
                        service_id: Number(serviceId),
                        name: item.service?.name || item.name || "",
                        fee: Number(fee) || 0,
                        quantity: Math.max(1, Number(quantity) || 1),
                        amount: Number(fee) || 0,
                        clinic_user_id: item.clinic_user_id ?? item.clinicUserId ?? null,
                    } as ServiceItem;
                })
                .filter(Boolean) as ServiceItem[];
            
            setSelectedServices(transformedServices);
        } catch (err) {
            console.log(err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSearch = (search: string) => {
        setSearchTerm(search);
        fetchServices(search);
    };

    const handleServiceSelect = (option: Option) => {
        if (selectedServices.some((s) => s.service_id.toString() === option.value)) return;

        const service = services.find((s) => s.id.toString() === option.value);
        if (!service) return;
        const serviceId = service.id ?? service.service_id;
        if (!serviceId) return;

        setSelectedServices([
            ...selectedServices,
            { 
                service_id: Number(serviceId), 
                name: service.name, 
                fee: service.fee || 0, 
                quantity: 1,
                amount: service.fee || 0,
                clinic_user_id: null,
            },
        ]);
    };

    const handleServiceRemove = (option: Option) => {
        setSelectedServices(
            selectedServices.filter((s) => s.service_id.toString() !== option.value)
        );
    };

    const handleServiceRowRemove = (serviceId: number) => {
        setSelectedServices((prev) => prev.filter((s) => s.service_id !== serviceId));
        if (openDropdownServiceId === serviceId) {
            setOpenDropdownServiceId(null);
        }
    };

    const handleQuantityChange = (serviceId: number, quantity: number) => {
        setSelectedServices((prev) =>
            prev.map((s) => {
                if (s.service_id === serviceId) {
                    const qty = Math.max(1, quantity || 1);
                    return { ...s, quantity: qty };
                }
                return s;
            })
        );
    };

    const handleAmountChange = (serviceId: number, amount: number) => {
        setSelectedServices((prev) =>
            prev.map((s) => {
                if (s.service_id === serviceId) {
                    const amt = Math.max(0, amount || 0);
                    return { ...s, fee: amt, amount: amt };
                }
                return s;
            })
        );
    };

    const handleClinicUserSelect = (serviceId: number, userId: number) => {
        setSelectedServices((prev) =>
            prev.map((s) => {
                if (s.service_id === serviceId) {
                    return { ...s, clinic_user_id: userId };
                }
                return s;
            })
        );
        setOpenDropdownServiceId(null);
        setClinicUserSearchTerm("");
    };

    const handleClinicUserRemove = (serviceId: number) => {
        setSelectedServices((prev) =>
            prev.map((s) => {
                if (s.service_id === serviceId) {
                    return { ...s, clinic_user_id: null };
                }
                return s;
            })
        );
    };

    const getFilteredClinicUsers = () => {
        if (!clinicUserSearchTerm.trim()) {
            return clinicUsers;
        }
        const searchLower = clinicUserSearchTerm.toLowerCase();
        return clinicUsers.filter(
            (user) =>
                user.firstName.toLowerCase().includes(searchLower) ||
                user.lastName.toLowerCase().includes(searchLower) ||
                (user.phone && user.phone.includes(searchLower))
        );
    };

    const handleSave = async () => {
        if (!visitId) {
            showNotification(
                "error",
                "Visit ID required",
                "Please ensure visit is loaded before saving services",
                "topRight"
            );
            return;
        }

        // Allow empty services array when updating (to remove all services)
        if (selectedServices.length === 0 && existingServices.length === 0) {
            showNotification(
                "error",
                "No services selected",
                "Please select at least one service",
                "topRight"
            );
            return;
        }

        try {
            setIsLoading(true);
            const invalid = selectedServices.find((s) => !s.service_id);
            if (invalid) {
                showNotification(
                    "error",
                    "Invalid service selection",
                    "Service ID is missing for one or more services. Please re-select the services.",
                    "topRight"
                );
                return;
            }

            // Prepare payload with all currently selected services
            // Backend update() method implements delete-and-add: 
            // It deletes ALL existing services for the visit and adds only the services in this payload
            const payload = {
                services: selectedServices.map((s) => ({
                    service_id: s.service_id,
                    fee: s.fee ?? 0,
                    quantity: s.quantity ?? 1,
                    clinic_user_id: s.clinic_user_id ?? null,
                })),
            };

            // Always use update endpoint when visitId exists
            // Backend will delete all existing services and add only the services in the payload
            const response: any = await updateVisitServices(visitId, payload);
            const savedServices = response.data || [];
            onServiceAdded(savedServices);
            // Update existing services state to reflect the new state from backend
            setExistingServices(savedServices);
            // Update selected services to match what's saved (in case backend modified anything)
            const transformedServices: ServiceItem[] = savedServices
                .map((item: any) => {
                    const serviceId =
                        item.service_id ??
                        item.serviceId ??
                        item.service?.id ??
                        item.service?.service_id;

                    if (!serviceId) return null;

                    const fee = item.fee ?? 0;
                    const quantity = item.quantity ?? 1;

                    return {
                        id: item.id,
                        service_id: Number(serviceId),
                        name: item.service?.name || item.name || "",
                        fee: Number(fee) || 0,
                        quantity: Math.max(1, Number(quantity) || 1),
                        amount: Number(fee) || 0,
                        clinic_user_id: item.clinic_user_id ?? item.clinicUserId ?? null,
                    } as ServiceItem;
                })
                .filter(Boolean) as ServiceItem[];
            setSelectedServices(transformedServices);
            onClose();
        } catch (err: any) {
            console.log(err);
            showNotification(
                "error",
                "Error saving services",
                err.response?.data?.message || "Failed to save services",
                "topRight"
            );
        } finally {
            setIsLoading(false);
        }
    };

    const getTotal = () =>
        selectedServices.reduce((acc, s) => acc + (s.fee || 0) * (s.quantity || 1), 0);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="bg-white rounded-xl shadow-lg w-full max-w-4xl h-[86vh] flex flex-col overflow-hidden">
                {/* Header */}
                <div className="px-6 py-4 border-b font-semibold text-lg bg-primary text-white flex justify-between items-center">
                    <span>Add Services</span>
                    <button
                        onClick={onClose}
                        className="text-white hover:text-gray-200 text-xl font-bold"
                    >
                        &times;
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-hidden flex flex-col px-6 py-4 mt-4 gap-4">
                    {/* Services Dropdown Section */}
                    <div className="mb-0">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Select Services
                        </label>
                        <div className="relative">
                            <MultiSelect
                                isOpen={isOpenDropdown}
                                toggleDropdown={() => setIsOpenDropdown(!isOpenDropdown)}
                                searchTerm={searchTerm}
                                onSearch={handleSearch}
                                onAddNewOption={() => { }}
                                options={services.map((s) => ({
                                    label: s.name,
                                    value: s.id.toString(),
                                }))}
                                selectedOptions={selectedServices.map((s) => ({
                                    label: s.name,
                                    value: s.service_id.toString(),
                                }))}
                                onOptionSelect={handleServiceSelect}
                                onOptionRemove={handleServiceRemove}
                                placeholder="Search and select services..."
                            />
                        </div>
                    </div>

                    {/* Services and Charges Block */}
                    <div className="flex-1 overflow-hidden flex flex-col mt-0">
                        <div className="bg-white rounded-lg border border-gray-300 flex flex-col h-full">
                            <label className="bg-[#ECEFFF] w-full px-4 py-2 text-base font-medium border-b block">
                                Services & Charges
                            </label>
                            <div className="flex-1 overflow-y-auto">
                                <table className="w-full">
                                    <thead className="bg-gray-50 sticky top-0 z-10">
                                        <tr>
                                            <th className="px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase min-w-[200px]">
                                                Description
                                            </th>
                                            <th className="px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                                                Quantity
                                            </th>
                                            <th className="px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                                                Amount
                                            </th>
                                            <th className="px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                                                Total
                                            </th>
                                            <th className="px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                                                
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {selectedServices.length > 0 ? (
                                            selectedServices.map((service, index) => (
                                                <tr key={service.service_id || index}>
                                                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                                        <div className="flex items-start gap-2 flex-wrap">
                                                            <span className="min-w-0 break-words">{service.name}</span>
                                                            <div className="relative inline-block clinic-user-dropdown-container flex-shrink-0">
                                                                {service.clinic_user_id ? (
                                                                    <div className="flex items-center gap-1">
                                                                        <div className="flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">
                                                                            <span>
                                                                                {clinicUsers.find(u => u.id === service.clinic_user_id)?.firstName} {clinicUsers.find(u => u.id === service.clinic_user_id)?.lastName}
                                                                            </span>
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => handleClinicUserRemove(service.service_id)}
                                                                                className="text-blue-700 hover:text-blue-900 font-bold ml-1"
                                                                            >
                                                                                &times;
                                                                            </button>
                                                                        </div>
                                                                        <a
                                                                            href="#"
                                                                            onClick={(e) => {
                                                                                e.preventDefault();
                                                                                setClinicUserSearchTerm("");
                                                                                setOpenDropdownServiceId(service.service_id);
                                                                            }}
                                                                            className="text-xs text-blue-600 hover:text-blue-800 underline cursor-pointer"
                                                                        >
                                                                            Add User
                                                                        </a>
                                                                        {openDropdownServiceId === service.service_id && (
                                                                            <div className="absolute left-0 top-full mt-1 w-48 bg-white border border-gray-300 rounded-md shadow-lg z-[100] max-h-48 flex flex-col overflow-hidden">
                                                                                <div className="px-3 py-2 text-xs font-medium text-gray-600 bg-gray-50 border-b">
                                                                                    Select clinic user
                                                                                </div>
                                                                                <input
                                                                                    type="text"
                                                                                    value={clinicUserSearchTerm}
                                                                                    onChange={(e) => setClinicUserSearchTerm(e.target.value)}
                                                                                    placeholder="Search users..."
                                                                                    className="w-full px-3 py-2 text-sm border-b border-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                                                    onClick={(e) => e.stopPropagation()}
                                                                                />
                                                                                <div className="overflow-y-auto overflow-x-hidden max-h-32">
                                                                                    {getFilteredClinicUsers().length > 0 ? (
                                                                                        getFilteredClinicUsers().map((user) => (
                                                                                            <button
                                                                                                key={user.id}
                                                                                                type="button"
                                                                                                onClick={() => {
                                                                                                    handleClinicUserSelect(service.service_id, user.id);
                                                                                                    setClinicUserSearchTerm("");
                                                                                                }}
                                                                                                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 break-words"
                                                                                            >
                                                                                                {user.firstName} {user.lastName}
                                                                                            </button>
                                                                                        ))
                                                                                    ) : (
                                                                                        <div className="px-3 py-2 text-sm text-gray-500 text-center">
                                                                                            No users found
                                                                                        </div>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                ) : (
                                                                    <>
                                                                        {openDropdownServiceId !== service.service_id && (
                                                                            <a
                                                                                href="#"
                                                                                onClick={(e) => {
                                                                                    e.preventDefault();
                                                                                    setClinicUserSearchTerm("");
                                                                                    setOpenDropdownServiceId(service.service_id);
                                                                                }}
                                                                                className="text-xs text-blue-600 hover:text-blue-800 underline cursor-pointer"
                                                                            >
                                                                                Add User
                                                                            </a>
                                                                        )}
                                                                        {openDropdownServiceId === service.service_id && (
                                                                            <div className="absolute left-0 top-full mt-1 w-48 bg-white border border-gray-300 rounded-md shadow-lg z-[100] max-h-48 flex flex-col overflow-hidden">
                                                                                <div className="px-3 py-2 text-xs font-medium text-gray-600 bg-gray-50 border-b">
                                                                                    Select clinic user
                                                                                </div>
                                                                                <input
                                                                                    type="text"
                                                                                    value={clinicUserSearchTerm}
                                                                                    onChange={(e) => setClinicUserSearchTerm(e.target.value)}
                                                                                    placeholder="Search users..."
                                                                                    className="w-full px-3 py-2 text-sm border-b border-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                                                    onClick={(e) => e.stopPropagation()}
                                                                                />
                                                                                <div className="overflow-y-auto overflow-x-hidden max-h-32">
                                                                                    {getFilteredClinicUsers().length > 0 ? (
                                                                                        getFilteredClinicUsers().map((user) => (
                                                                                            <button
                                                                                                key={user.id}
                                                                                                type="button"
                                                                                                onClick={() => {
                                                                                                    handleClinicUserSelect(service.service_id, user.id);
                                                                                                    setClinicUserSearchTerm("");
                                                                                                }}
                                                                                                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 break-words"
                                                                                            >
                                                                                                {user.firstName} {user.lastName}
                                                                                            </button>
                                                                                        ))
                                                                                    ) : (
                                                                                        <div className="px-3 py-2 text-sm text-gray-500 text-center">
                                                                                            No users found
                                                                                        </div>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                    </>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-gray-900">
                                                        <input
                                                            type="number"
                                                            min={1}
                                                            value={service.quantity}
                                                            onChange={(e) => {
                                                                const qty = parseInt(e.target.value) || 1;
                                                                handleQuantityChange(service.service_id, qty);
                                                            }}
                                                            className="w-20 text-center border rounded px-2 py-1"
                                                        />
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-gray-900">
                                                        <input
                                                            type="number"
                                                            min={0}
                                                            step="0.01"
                                                            value={service.fee}
                                                            onChange={(e) => {
                                                                const amt = parseFloat(e.target.value) || 0;
                                                                handleAmountChange(service.service_id, amt);
                                                            }}
                                                            className="w-24 text-center border rounded px-2 py-1"
                                                        />
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-gray-900">
                                                        ₹{((service.fee || 0) * (service.quantity || 1)).toFixed(2)}
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-gray-900">
                                                        <button
                                                            type="button"
                                                            onClick={() => handleServiceRowRemove(service.service_id)}
                                                            className="text-gray-500 hover:text-red-600 font-bold text-lg"
                                                            aria-label="Remove service"
                                                            title="Remove service"
                                                        >
                                                            &times;
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan={5} className="px-4 py-4 text-sm text-center text-gray-500">
                                                    No services added
                                                </td>
                                            </tr>
                                        )}
                                        <tr className="font-semibold bg-gray-50 sticky bottom-0">
                                            <td className="px-4 py-3 text-sm text-gray-900">Total</td>
                                            <td className="px-4 py-3"></td>
                                            <td className="px-4 py-3"></td>
                                            <td className="px-4 py-3 text-sm text-gray-900">
                                                ₹{getTotal().toFixed(2)}
                                            </td>
                                            <td className="px-4 py-3"></td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="flex justify-end gap-2 px-6 py-4 border-t">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm rounded-md border"
                        disabled={isLoading}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={isLoading || (selectedServices.length === 0 && existingServices.length === 0)}
                        className="px-4 py-2 text-sm text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isLoading ? "Saving..." : "Save"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ServiceModal;
